import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * hallmark critique · VehicleLayer
 * tokens locked from docs/design.md §10 (Vehicle Marker spec)
 * states covered: default · bunched (clay-ember halo) · held (cool-ash pulse)
 *                 + focused (Zepto-style single-select — this one, big, alone)
 * mobile verified: 320 / 375 / 414 / 768
 *
 * Phase 13g — hardening the PNG bus marker.
 *
 * Previous bug: <img width="36" height="36"> inside Leaflet's L.divIcon was
 * rendering at native 512×512 because Leaflet's default `.leaflet-div-icon`
 * CSS forces `background:#fff;border:1px solid #666;width:auto;height:auto`
 * on the wrapper, which cascades to the child img via user-agent styles.
 *
 * Fix: use a fixed-size <div> with `background: url()` (guaranteed sizing,
 * no img element), plus explicit globals.css overrides on `.bb-bus-marker`.
 * Halo rendered as sibling ::before-style absolute div so it can pulse.
 *
 * Bearing behaviour: icon stays UPRIGHT (Zepto delivery-agent pattern),
 * horizontally mirrored via scaleX(-1) when heading west (bearing 180-360°)
 * so the bus always visually faces the direction of travel.
 */
import { Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
const BUS_ICON_URL = "/bus.svg";
function iconGeometry(hint, bearing, focused) {
    // Phase 17c fix: non-focused footprint tightened to prevent clay-ember
    // blob-effect when 30+ buses cluster on the Mumbai city-wide overview.
    //   base 26→22, halo 40→26 (barely wraps the icon)
    //   halo fill 0.22→0.14, border 0.9→0.55
    // Focused variant is unchanged — selected bus must still read as bold.
    // Phase 17-poka2: user asked for bigger icons so the dashboard reads as a
    // proper control tower, not a heatmap. Base bumped 22→32, halo 26→40
    // (still tighter than the pre-Phase-17c 40/40 default).
    const base = focused ? 52 : 32;
    const halo = focused ? 76 : hint === "bunched" ? 40 : hint === "held" ? 40 : 0;
    const wrap = Math.max(base + 4, halo);
    const flipX = bearing > 180 && bearing < 360;
    const haloBg = focused
        ? "rgba(188,113,85,0.22)"
        : hint === "bunched"
            ? "rgba(188,113,85,0.14)"
            : hint === "held"
                ? "rgba(142,142,149,0.14)"
                : "transparent";
    const haloBorder = focused
        ? "2px solid rgba(188,113,85,0.9)"
        : hint === "bunched"
            ? "2px solid rgba(188,113,85,0.55)"
            : hint === "held"
                ? "2px dashed rgba(142,142,149,0.55)"
                : "0";
    const pulseClass = focused ? "bb-bus-pulse" : "";
    const zIndex = focused ? 9999 : hint === "bunched" ? 900 : 500;
    return { base, halo, wrap, flipX, haloBg, haloBorder, pulseClass, zIndex };
}
function busDivIcon(hint, bearing, focused) {
    const g = iconGeometry(hint, bearing, focused);
    // Top-down bus SVG points north when unrotated. Rotate the icon body by
    // the GTFS-RT bearing so the bus visually faces its direction of travel
    // on the map. Halo + wrapper stay unrotated so bunched/held rings read
    // as concentric circles regardless of heading.
    const flipTransform = `rotate(${bearing}deg)`;
    // NOTE: use background-image (not <img>) so the size is dictated by the
    // div's inline width/height and cannot be overridden by any UA/CSS rule.
    return L.divIcon({
        className: `bb-bus-marker bb-bus-${hint}${focused ? " bb-bus-focused" : ""}`,
        html: `
      <div class="bb-bus-outer" style="
        position:relative;
        width:${g.wrap}px;
        height:${g.wrap}px;
        display:flex;
        align-items:center;
        justify-content:center;
        z-index:${g.zIndex};
        pointer-events:auto;
      ">
        ${g.halo > 0
            ? `<div class="${g.pulseClass}" aria-hidden="true" style="
                position:absolute;
                width:${g.halo}px;
                height:${g.halo}px;
                border-radius:50%;
                background:${g.haloBg};
                border:${g.haloBorder};
                pointer-events:none;
              "></div>`
            : ""}
        <div class="bb-bus-icon" style="
          position:relative;
          width:${g.base}px;
          height:${g.base}px;
          background-image:url('${BUS_ICON_URL}');
          background-repeat:no-repeat;
          background-position:center;
          background-size:contain;
          transform:${flipTransform};
          filter:drop-shadow(0 1px 2px rgba(0,0,0,0.3));
          pointer-events:auto;
        "></div>
      </div>
    `,
        iconSize: [g.wrap, g.wrap],
        iconAnchor: [g.wrap / 2, g.wrap / 2],
    });
}
export function VehicleLayer({ vehicles, demandActive = false, onBusClick, focusedVehicleId = null, }) {
    const rendered = focusedVehicleId
        ? vehicles.filter((v) => v.vehicle.id === focusedVehicleId)
        : vehicles;
    return (_jsx(_Fragment, { children: rendered.map((v) => {
            const hint = demandActive ? "normal" : v.ui_hint;
            const focused = v.vehicle.id === focusedVehicleId;
            const label = focused
                ? `${v.vehicle.id} · SELECTED`
                : hint === "held"
                    ? `${v.vehicle.id} · HELD · click for detail`
                    : hint === "bunched"
                        ? `${v.vehicle.id} · BUNCHING · click for detail`
                        : `${v.vehicle.id}`;
            return (_jsx(Marker, { position: [v.position.lat, v.position.lng], icon: busDivIcon(hint, v.position.bearing, focused), eventHandlers: onBusClick
                    ? {
                        click: () => onBusClick(v),
                        keydown: (e) => {
                            const ke = e.originalEvent;
                            if (ke.key === "Enter" || ke.key === " ")
                                onBusClick(v);
                        },
                    }
                    : undefined, children: _jsx(Tooltip, { direction: "top", offset: [0, -Math.max(20, focused ? 36 : 18)], opacity: 1, children: label }) }, v.entity_id));
        }) }));
}
